/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-destructuring */
import React, {
  FormEvent,
  useCallback,
  useState,
  useEffect,
} from 'react';
import {
  SelectionMode,
  DetailsListLayoutMode,
  Stack,
  IComboBoxOption,
  ActionButton,
  IconButton,
  Modal,
  TextField,
  Icon,
  Selection,
  IDragDropEvents,
  IDragDropContext,
  VirtualizedComboBox,
  Checkbox,
  ComboBox,
  Toggle,
  DetailsRow,
  IDetailsRowProps,
  IColumn,
  DetailsList,
  IDetailsRowStyles,
  IStackStyles,
  IStyleFunctionOrObject,
  IDetailsRowStyleProps,
} from '@fluentui/react';
import { ShimmeredDetailsList } from '@fluentui/react/lib/ShimmeredDetailsList';
import _ from 'lodash';
import {
  getTheme,
  mergeStyles,
  mergeStyleSets,
} from '@fluentui/react/lib/Styling';
import moment from 'moment';
import {
  convertSalesRepsToComboBoxOptions,
  convertSuppliersToDropdownOptions,
  ISalesRep,
  IPropertySupplier,
  convertEmployeesToComboBoxOptions,
  IEmployee,
  stackTokens15,
  modalContentStyles,
  iconButtonStyles,
  cancelIcon,
  overviewControlStyles,
  sortComboBoxOptionOnText,
  iconLastProps,
  ISupplier,
  IPurchaseOrder,
} from '../../../utils';
import { IProperty } from '../../../utils/Property';
import ConstructionSheetRemarks from './ConstructionSheetRemarks';
import ConstructionSheetDatePicker from './ConstructionSheetDatePicker';
import ConstructionSheetCombobox from './ConstructionSheetCombobox';

const theme = getTheme();

const dragEnterClass = mergeStyles({
  backgroundColor: theme.palette.neutralLight,
});

// Modern glass-like styles
const glassContainerStyles: IStackStyles = {
  root: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    borderRadius: '8px',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '15px',
    margin: '15px 0',
  },
};

const expandedRowStyles = mergeStyleSets({
  root: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(5px)',
    borderRadius: '4px',
    margin: '8px 0',
    padding: '10px',
    boxShadow: 'inset 0 2px 5px rgba(0, 0, 0, 0.05)',
  },
  header: {
    fontWeight: 600,
    color: theme.palette.themePrimary,
    marginBottom: '8px',
  },
  detailsContainer: {
    padding: '0 15px',
  },
  poDetailsList: {
    background: 'transparent',
  },
  poDetailsHeader: {
    background: 'rgba(255, 255, 255, 0.1)',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '5px 0',
  },
  poDetailsRow: {
    borderRadius: '4px',
    margin: '4px 0',
    ':hover': {
      background: 'rgba(255, 255, 255, 0.1)',
    },
  },
});

const getStatusColor = (status: string): string => 'rgba(161, 159, 157, 0.8)';

const getStatusPillStyles = (status: any): string => {
  // Convert status to string to ensure compatibility
  const statusStr =
    typeof status === 'object'
      ? status.label || 'unknown'
      : String(status || 'unknown');

  return mergeStyles({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    background: getStatusColor(statusStr),
    color: 'white',
  });
};

// New function to get row status and styling
const getRowStatus = (item: IProperty) => {
  if (item.is_not_applicable) {
    return {
      text: '',
      color: '#d13438', // Red
      background: 'rgba(209, 52, 56, 0.2)',
    };
  }

  if (!item.order_date) {
    return {
      text: '',
      color: '#ffaa44', // Yellow/Orange
      background: 'rgba(255, 170, 68, 0.2)',
    };
  }

  return {
    text: '',
    color: '#107c10', // Green
    background: 'rgba(16, 124, 16, 0.2)',
  };
};

const getRowStatusPillStyles = (item: IProperty) => {
  const status = getRowStatus(item);

  return mergeStyles({
    position: 'absolute',
    bottom: '0px',
    left: '0px',
    width: '2px',
    height: '2px',
    borderRadius: '20%',
    background: status.color,
    border: 'none',
    zIndex: 1,
  });
};

// Safely convert status object to string
const getStatusDisplay = (status: any): string => {
  if (!status) return 'Unknown';

  if (typeof status === 'object') {
    return status.label || 'Unknown';
  }

  return String(status);
};

const customRowStyles: Partial<IDetailsRowStyles> = {
  root: {
    selectors: {
      ':hover': {
        background: 'rgba(255, 255, 255, 0.08)',
      },
    },
    borderRadius: '4px',
    margin: '2px 0',
  },
  cell: {
    padding: '10px',
  },
};

interface Props {
  callBackProperties: any;
  employees: any;
  properties: IProperty[];
  salesreps: any;
  suggestedvalues: any;
  suppliers: any;
  editItem: () => void;
  setSelectedPropertyID: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  setFilter: React.Dispatch<string>;
  filter?: string;
  moveItem: (a: IProperty, b: IProperty) => void;
  onOpenPurchaseOrder?: (purchaseOrderId: number) => void;
  onCopyPurchaseOrder?: (purchaseOrderId: number) => void;
}

// Interface for purchase order details - using only for reference, not extending actual IPurchaseOrder
interface IPurchaseOrderDetail {
  id: number;
  date_created?: Date;
  status?: string;
  delivery_date?: Date;
  supplier?: ISupplier;
  description?: string;
}

const ConstructionSheetOverview: React.FC<Props> = ({
  //   callBack,
  callBackProperties,
  employees,
  //   id,
  editItem,
  properties,
  //   refetch,
  salesreps,
  suggestedvalues,
  suppliers,
  setFilter,
  setSelectedPropertyID,
  filter,
  moveItem,
  onOpenPurchaseOrder,
  onCopyPurchaseOrder,
}) => {
  const [draggedItem, setDraggedItem] = useState<{
    property?: IProperty;
    index: number;
  }>({ property: undefined, index: -1 });

  const [enableDragDrop, setEnableDragDrop] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [scrollInterval, setScrollInterval] = useState<number | null>(null);
  const lastScrollPosition = React.useRef(0);
  const currentSpeed = React.useRef(0);
  const lastFrameTime = React.useRef(0);

  // Improved easing function for smoother acceleration/deceleration
  const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - 2 ** (-10 * t));

  // Enhanced scroll function with better frame timing
  const scrollContainer = (targetSpeed: number) => {
    const now = performance.now();
    const deltaTime = now - lastFrameTime.current;
    lastFrameTime.current = now;

    // Smoother acceleration with time-based interpolation
    const acceleration = 0.15; // Reduced for smoother acceleration
    const frameFactor = Math.min(deltaTime / 16.67, 2); // Normalize to 60fps
    currentSpeed.current +=
      (targetSpeed - currentSpeed.current) * acceleration * frameFactor;

    // Apply improved easing
    const easedSpeed =
      currentSpeed.current * easeOutExpo(Math.abs(currentSpeed.current) / 30);

    // Apply scroll with sub-pixel precision
    window.scrollBy({
      top: easedSpeed,
      behavior: 'auto',
    });
    lastScrollPosition.current = window.scrollY;
  };

  // Cleanup function
  const stopScrolling = () => {
    if (scrollInterval) {
      window.cancelAnimationFrame(scrollInterval);
      setScrollInterval(null);
    }
    currentSpeed.current = 0;
    lastFrameTime.current = 0;
  };

  const toggleExpand = (propertyId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(propertyId)) {
      newExpandedRows.delete(propertyId);
    } else {
      newExpandedRows.add(propertyId);
    }
    setExpandedRows(newExpandedRows);
  };

  const onSelectionChanged = () => {
    const selection: any = _selection.getSelection();
    if (selection && selection.length > 0) {
      if (selection[0].id) setSelectedPropertyID(selection[0].id);
    } else {
      setSelectedPropertyID(undefined);
    }
  };

  const [_selection] = useState(
    new Selection({
      onSelectionChanged,
    }),
  );

  const getKey = (item: any) => {
    if (item) return item.id;
    return 0;
  };

  // Constants
  const optionsEmployees = convertEmployeesToComboBoxOptions(employees);
  const optionsSalesReps = convertSalesRepsToComboBoxOptions(salesreps);
  const optionsSuppliers = convertSuppliersToDropdownOptions(suppliers);

  const filteredOptionsSalesReps = optionsSalesReps.filter(
    (salesRep: IComboBoxOption) =>
      optionsEmployees
        .map((employee: IComboBoxOption) => employee.text)
        .indexOf(salesRep.text) === -1,
  );

  const responsibleOptions = [...optionsEmployees].sort(
    sortComboBoxOptionOnText,
  );

  // Hooks
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IProperty>();

  const getSelectedSuppliers = (property: IProperty) => {
    const supplierIdsFromSuppliers = property.suppliers
      ? property.suppliers
          .filter(el => !el.delete)
          .map((el: IPropertySupplier) => el.supplier.id)
      : [];

    const supplierIdsFromPurchaseOrders = property.purchase_orders
      ? property.purchase_orders
          .filter((order: IPurchaseOrder) => order.supplier !== undefined)
          .map((order: IPurchaseOrder) => order.supplier!.id)
      : [];

    // Combine both arrays and remove duplicates
    const combinedSupplierIds = Array.from(
      new Set([...supplierIdsFromSuppliers, ...supplierIdsFromPurchaseOrders]),
    );

    return combinedSupplierIds;
  };

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue || '';

    setFilter(filterString);
  };

  const updateDate = (item: IProperty) => {
    const newProperties = properties.map((prop: IProperty) =>
      (prop.id === item.id ? item : prop));
    // setListItems(newProperties);
    callBackProperties(newProperties);
  };

  const updateExecutionDate = useCallback(
    (id: number, checked: boolean) => {
      const newProperties = _.cloneDeep(properties);
      for (let i = 0; i < newProperties.length; i++) {
        if (newProperties[i].id === id) {
          newProperties[i].execution_date = checked
            ? moment().toDate()
            : undefined;
          newProperties[i].isDirty = true;
        }
      }
      callBackProperties(newProperties);
    },
    [properties, callBackProperties],
  );

  const updateResponsible = (id: number, item?: IEmployee) => {
    let filteredProperties = _.cloneDeep(properties);
    filteredProperties = filteredProperties.filter(
      (property: IProperty) => property.id === id,
    );

    const updatedProperty = filteredProperties[0];

    updatedProperty.isDirty = true;

    updatedProperty.employee = item;

    const newProperties = properties.map((property: IProperty) =>
      (property.id === id ? updatedProperty : property));
    // setListItems(newProperties);
    callBackProperties(newProperties);
  };

  // ids: the collection of selected supplierId's
  const updateSuppliers = (id: number, option: IComboBoxOption | undefined) => {
    if (!option) return;
    const currentProperties = _.cloneDeep(properties);
    let isCurrentSupplier = false;
    for (let i = 0; i < currentProperties.length; i += 1) {
      if (currentProperties[i].id === id) {
        // current property
        currentProperties[i].isDirty = true;
        const currentSuppliers =
          _.cloneDeep(currentProperties[i].suppliers) || [];
        for (let e = 0; e < currentSuppliers.length; e += 1) {
          if (option.key === currentSuppliers[e].supplier.id) {
            isCurrentSupplier = true;
            if (option.selected) {
              currentSuppliers[e].delete = false;
              currentSuppliers[e].dirty = true;
            } else {
              currentSuppliers[e].delete = true;
              currentSuppliers[e].dirty = true;
            }
          }
        }

        if (!isCurrentSupplier) {
          const supplierID = option.key as number; // type error to fix
          const supplier: ISupplier | undefined = suppliers.filter(
            (supplier: ISupplier) => supplier.id === supplierID,
          )[0];

          if (supplier) {
            currentSuppliers.push({
              id: -1,
              supplier,
              new: true,
              dirty: true,
            });
          }
        }

        currentProperties[i].suppliers = currentSuppliers;
      }
    }

    // setListItems(newProperties);
    callBackProperties(currentProperties);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const getOptions = (item: IProperty) => {
    const filteredEmployees = employees.filter(
      (employee: any) =>
        employee.active || (item.employee && item.employee.id === employee.id),
    );

    const optionsEmployeez =
      convertEmployeesToComboBoxOptions(filteredEmployees);
    return [...optionsEmployeez].sort(sortComboBoxOptionOnText);
  };

  // Add a new function to group related purchase orders
  const groupPurchaseOrdersByStatus = (purchaseOrders: any[]) => {
    if (!purchaseOrders || purchaseOrders.length === 0) return {};

    const groupedOrders: { [key: string]: any[] } = {};

    purchaseOrders.forEach(po => {
      const statusKey =
        typeof po.status === 'object'
          ? po.status.label || 'unknown'
          : String(po.status || 'unknown');
      if (!groupedOrders[statusKey]) {
        groupedOrders[statusKey] = [];
      }
      groupedOrders[statusKey].push(po);
    });

    return groupedOrders;
  };

  // Create a proper Purchase Order row component
  const PurchaseOrderRow = ({ po }: { po: any }) => {
    const statusText =
      typeof po.status === 'object'
        ? po.status.label || 'Onbekend'
        : po.status || 'Onbekend';

    // Use the correct property names: creation_date and delivery_date
    const creationDate = po.creation_date || po.date_created || po.created_at;
    const deliveryDate = po.delivery_date;

    // Format dates or provide default
    const formattedCreationDate = creationDate
      ? moment(creationDate).format('DD/MM/YYYY')
      : '-';
    const formattedDeliveryDate = deliveryDate
      ? moment(deliveryDate).format('DD/MM/YYYY')
      : '-';

    return (
      <div
        className={mergeStyles({
          display: 'grid',
          gridTemplateColumns: '110px 140px 100px 120px 120px auto',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 15px',
          margin: '2px 0',
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderLeft: `4px solid ${getStatusColor(statusText)}`,
          borderRadius: '0 4px 4px 0',
        })}
      >
        <div>
          <div
            className={mergeStyles({
              fontSize: '11px',
              color: theme.palette.neutralSecondary,
              marginBottom: '4px',
            })}
          >
            {po.is_purchase_order === false ? 'Prijsvraag' : 'Bestelbon'} nr.
          </div>
          <div className={mergeStyles({ lineHeight: '20px' })}>
            {po.purchase_order_no || po.id || '-'}
          </div>
        </div>

        <div>
          <div
            className={mergeStyles({
              fontSize: '11px',
              color: theme.palette.neutralSecondary,
              marginBottom: '4px',
            })}
          >
            Leverancier
          </div>
          <div
            className={mergeStyles({
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '20px',
            })}
          >
            {po.supplier?.name || '-'}
          </div>
        </div>

        <div>
          <div
            className={mergeStyles({
              fontSize: '11px',
              color: theme.palette.neutralSecondary,
              marginBottom: '4px',
            })}
          >
            Status
          </div>
          <div
            className={mergeStyles({
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
            })}
          >
            <span className={getStatusPillStyles(statusText)}>
              {statusText}
            </span>
          </div>
        </div>

        <div>
          <div
            className={mergeStyles({
              fontSize: '11px',
              color: theme.palette.neutralSecondary,
              marginBottom: '4px',
            })}
          >
            Aangemaakt
          </div>
          <div
            className={mergeStyles({
              lineHeight: '20px',
            })}
          >
            {formattedCreationDate}
          </div>
        </div>

        <div>
          <div
            className={mergeStyles({
              fontSize: '11px',
              color: theme.palette.neutralSecondary,
              marginBottom: '4px',
            })}
          >
            Levering
          </div>
          <div
            className={mergeStyles({
              lineHeight: '20px',
            })}
          >
            {formattedDeliveryDate}
          </div>
        </div>

        {po.id && (onOpenPurchaseOrder || onCopyPurchaseOrder) ? (
          <div className={mergeStyles({ display: 'flex', gap: '4px', justifyContent: 'flex-start' })}>
            {onOpenPurchaseOrder && (
              <ActionButton
                iconProps={{ iconName: 'Edit' }}
                ariaLabel='Bestelbon openen'
                onClick={e => {
                  e.stopPropagation();
                  onOpenPurchaseOrder(po.id);
                }}
              >
                Openen
              </ActionButton>
            )}
            {onCopyPurchaseOrder && (
              <ActionButton
                iconProps={{ iconName: 'Copy' }}
                ariaLabel='Bestelbon kopiëren'
                title='Kopie maken, bv. prijsvraag voor een andere leverancier'
                onClick={e => {
                  e.stopPropagation();
                  onCopyPurchaseOrder(po.id);
                }}
              >
                Kopiëren
              </ActionButton>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  // Custom row rendering to show purchase order details when expanded
  const onRenderRow = (props?: IDetailsRowProps): JSX.Element | null => {
    if (!props) return null;

    const { item } = props;
    const isExpanded = expandedRows.has(item.id);
    const hasPurchaseOrders =
      item.purchase_orders && item.purchase_orders.length > 0;

    return (
      <div>
        <DetailsRow {...props} styles={customRowStyles} />
        {isExpanded && hasPurchaseOrders && !item.is_not_applicable && (
          <div
            className={mergeStyles({
              padding: '0 0 10px 25px',
            })}
          >
            <div
              className={mergeStyles({
                fontSize: '12px',
                fontWeight: 600,
                color: theme.palette.themePrimary,
                margin: '10px 0 5px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              })}
            >
              <Icon
                iconName='Shopping'
                styles={{ root: { fontSize: '14px' } }}
              />
              Bestelbonnen ({item.purchase_orders.length})
            </div>

            {item.purchase_orders.map((po: any, idx: number) => (
              <PurchaseOrderRow key={po.id || idx} po={po} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const columns = [
    {
      key: 'column0',
      name: '',
      fieldName: 'expand',
      minWidth: 35,
      maxWidth: 35,
      isRowHeader: false,
      isResizable: false,
      onRender: (item: IProperty) => {
        const hasPurchaseOrders =
          item.purchase_orders &&
          item.purchase_orders.length > 0 &&
          !item.is_not_applicable;
        if (!hasPurchaseOrders) return null;

        return (
          <IconButton
            iconProps={{
              iconName: expandedRows.has(item.id)
                ? 'ChevronDown'
                : 'ChevronRight',
            }}
            onClick={() => toggleExpand(item.id)}
            styles={{
              root: {
                color: theme.palette.themePrimary,
                margin: 0,
                padding: 0,
              },
            }}
            aria-label='Toggle purchase orders'
          />
        );
      },
      isPadded: false,
    },
    {
      key: 'column1',
      name: 'Omschrijving',
      fieldName: 'name',
      minWidth: 250,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      onRender: (item: IProperty, index?: number) => (
        <div style={{ position: 'relative', paddingRight: '70px', backgroundColor: getRowStatus(item).background }}>
          <span
            style={
              item.delete
                ? { opacity: 0.5, textDecoration: 'line-through' }
                : undefined
            }
          >
            <strong>
              {item.name}
              {item.isDirty ? '*' : null}
              {item.is_not_applicable ? ' (NVT)' : null}
            </strong>
          </span>

          {item.purchase_orders &&
            item.purchase_orders.length > 0 &&
            !item.is_not_applicable && (
              <span
                className={mergeStyles({
                  marginLeft: '8px',
                  fontSize: '11px',
                  padding: '2px 6px',
                  background: 'rgba(0, 120, 212, 0.1)',
                  color: theme.palette.themePrimary,
                  borderRadius: '10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                })}
              >
                <Icon
                  iconName='DocumentSet'
                  styles={{ root: { fontSize: '10px' } }}
                />
                {item.purchase_orders.length}
              </span>
            )}

        </div>
      ),
      data: 'number',
      isPadded: false,
    },
    {
      key: 'column2',
      name: 'Besteld',
      fieldName: 'order_date',
      minWidth: 125,
      maxWidth: 125,
      isRowHeader: true,
      isResizable: false,
      onRender: (item: IProperty) =>
        (item.is_not_applicable ? (
          <span />
        ) : (
          <ConstructionSheetDatePicker
            canEmpty
            property={item}
            target='order_date'
            callBack={updateDate}
          />
        )),
      data: 'string',
      isPadded: false,
    },
    {
      key: 'column4',
      name: 'Verantwoordelijke',
      fieldName: '',
      minWidth: 125,
      maxWidth: 125,
      isRowHeader: true,
      isResizable: false,
      onRender: (item: IProperty) =>
        (item.is_not_applicable ? (
          <span />
        ) : (
          <ComboBox
            options={getOptions(item)}
            selectedKey={
              item.employee && item.employee.id && item.employee.id > 0
                ? item.employee.id
                : -1
            }
            onChange={(e, option) => {
              if (employees && employees.length > 0 && option) {
                for (let i = 0; i < employees.length; i++) {
                  if (employees[i].id === option.key) {
                    updateResponsible(item.id, employees[i]);
                    return;
                  }
                }
              } else {
                updateResponsible(item.id);
              }
            }}
            allowFreeform
            styles={{
              root: {
                width: '100%',
              },
              input: {
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
              },
            }}
          />
        )),
      data: 'string',
      isPadded: false,
    },
    {
      key: 'column5',
      name: 'OA./leverancier',
      fieldName: '',
      minWidth: 150,
      maxWidth: 150,
      isRowHeader: true,
      isResizable: false,
      onRender: (item: IProperty) =>
        (item.is_not_applicable ? (
          <span />
        ) : (
          <ComboBox
            options={optionsSuppliers}
            multiSelect
            placeholder='Kies leverancier(s)'
            autoComplete='on'
            onChange={(event, option, index, value) => {
              updateSuppliers(item.id, option);
            }}
            selectedKey={getSelectedSuppliers(item)}
            allowFreeform
            styles={{
              root: {
                width: '100%',
              },
              input: {
                backgroundColor: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
              },
            }}
          />
        )),
      data: 'string',
      isPadded: false,
    },
    {
      key: 'column6',
      name: 'Voorzien',
      fieldName: 'due_date',
      minWidth: 125,
      maxWidth: 125,
      isRowHeader: true,
      isResizable: true,
      onRender: (item: IProperty) =>
        (item.is_not_applicable ? (
          <span />
        ) : (
          <ConstructionSheetDatePicker
            canEmpty
            property={item}
            target='due_date'
            callBack={updateDate}
          />
        )),
      data: 'string',
      isPadded: false,
    },
    {
      key: 'column7',
      name: 'Uitgevoerd',
      fieldName: 'execution_date',
      minWidth: 75,
      maxWidth: 75,
      isRowHeader: true,
      isResizable: false,
      onRender: (item: IProperty) =>
        (item.is_not_applicable ? (
          <span />
        ) : (
          <Checkbox
            checked={!!item.execution_date}
            onChange={e => {
              // @ts-ignore
              const { target } = e;
              const { checked } = target;
              updateExecutionDate(item.id, checked);
            }}
          />
        )),
      data: 'string',
      isPadded: false,
    },
    {
      key: 'column8',
      name: 'Opmerkingen',
      fieldName: 'value',
      minWidth: 200,
      maxWidth: 200,
      isRowHeader: true,
      isResizable: false,
      onRender: (item: IProperty) =>
        (item.is_not_applicable ? (
          <span />
        ) : (
          <ConstructionSheetRemarks
            item={item}
            suggestedvalues={suggestedvalues}
            horizontal
          />
        )),
      data: 'string',
      isPadded: false,
    },
  ];

  const _insertBeforeItem = (item: IProperty) => {
    const draggedItems = _selection.isIndexSelected(draggedItem.index)
      ? (_selection.getSelection() as IProperty[])
      : [draggedItem.property];

    if (draggedItems && draggedItems[0]) {
      moveItem(draggedItems[0], item);
      _selection.setAllSelected(false);
    }
  };

  const _getDragDropEvents = useCallback(
    (): IDragDropEvents => ({
      canDrop: () => true,
      canDrag: (item?: any) => enableDragDrop,
      onDragEnter: () => dragEnterClass,
      onDragLeave: () => {},
      onDrop: (item?: any) => {
        if (draggedItem) {
          _insertBeforeItem(item);
        }
        setIsDragging(false);
        stopScrolling();
        requestAnimationFrame(() => {
          window.scrollTo(0, lastScrollPosition.current);
        });
      },
      onDragStart: (item?: any, itemIndex?: number) => {
        setDraggedItem({
          property: item,
          index: itemIndex!,
        });
        setIsDragging(true);
        lastScrollPosition.current = window.scrollY;
        currentSpeed.current = 0;
        lastFrameTime.current = performance.now();
      },
      onDragEnd: () => {
        setDraggedItem({
          property: undefined,
          index: -1,
        });
        setIsDragging(false);
        stopScrolling();
        requestAnimationFrame(() => {
          window.scrollTo(0, lastScrollPosition.current);
        });
      },
      onDragOver: (item?: any, event?: DragEvent) => {
        if (!event || !isDragging) return;

        const viewportHeight = window.innerHeight;
        const scrollThreshold = 150;
        const maxScrollSpeed = 35; // Increased for smoother feel
        const clientY = event.clientY;
        const distanceFromBottom = viewportHeight - clientY;

        // Get window scroll info
        const scrollTop = window.scrollY;
        const scrollHeight = document.documentElement.scrollHeight;
        const isNearBottom = scrollHeight - (scrollTop + viewportHeight) < 150;

        let targetSpeed = 0;

        if (clientY < scrollThreshold) {
          // Scroll up with smooth acceleration
          const edgeProximity = 1 - clientY / scrollThreshold;
          targetSpeed = -maxScrollSpeed * edgeProximity;
        } else if (distanceFromBottom < scrollThreshold || isNearBottom) {
          // Scroll down with smooth acceleration
          const edgeProximity = 1 - distanceFromBottom / scrollThreshold;
          targetSpeed = maxScrollSpeed * edgeProximity;
        }

        if (targetSpeed !== 0) {
          // Apply scroll immediately
          scrollContainer(targetSpeed);

          // Set up continuous scrolling with improved frame timing
          if (!scrollInterval) {
            const scroll = () => {
              scrollContainer(targetSpeed);
              const newInterval = window.requestAnimationFrame(scroll);
              setScrollInterval(newInterval);
            };
            const newInterval = window.requestAnimationFrame(scroll);
            setScrollInterval(newInterval);
          }
        } else {
          stopScrolling();
        }
      },
    }),
    [enableDragDrop, draggedItem, isDragging, scrollInterval],
  );

  // Cleanup on unmount
  useEffect(
    () => () => {
      stopScrolling();
    },
    [],
  );

  const sortProperties = (propertiesToSort: IProperty[]) => {
    const sorted = propertiesToSort.sort(
      (a, b) =>
        a.weight_for_construction_sheet - b.weight_for_construction_sheet,
    );

    return sorted;
  };

  // Add this new function to handle keyboard navigation
  const handleDetailsListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      const activeElement = document.activeElement;
      if (!activeElement) return;

      // Find the current cell - look for the closest cell with data-automation-key
      const currentCell = activeElement.closest('[data-automation-key]');
      if (!currentCell) return;

      // Find the current row - look for the closest DetailsRow
      const currentRow = currentCell.closest('.ms-DetailsRow');
      if (!currentRow) return;

      // Get all cells in the current row - look for cells with data-automation-key
      const cells = currentRow.querySelectorAll('[data-automation-key]');
      const currentIndex = Array.from(cells).indexOf(currentCell);

      // Get all rows in the details list
      const allRows = document.querySelectorAll('.ms-DetailsRow');
      const currentRowIndex = Array.from(allRows).indexOf(currentRow);

      // Find the next/previous cell
      const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;

      if (nextIndex >= 0 && nextIndex < cells.length) {
        // Stay within the same row
        e.preventDefault();
        const nextCell = cells[nextIndex];
        // Look for interactive elements within the cell
        const nextInput = nextCell.querySelector(
          'input, select, button, [role="combobox"], [data-is-focusable="true"]',
        );
        if (nextInput) {
          (nextInput as HTMLElement).focus();
        } else if (!e.shiftKey && currentRowIndex < allRows.length - 1) {
          // If no input found in this cell, try to move to next row
          const nextRow = allRows[currentRowIndex + 1];
          const nextRowCells = nextRow.querySelectorAll('[data-automation-key]');
          if (nextRowCells.length > 0) {
            for (let i = 0; i < nextRowCells.length; i++) {
              const firstCell = nextRowCells[i];
              const firstInput = firstCell.querySelector(
                'input, select, button, [role="combobox"], [data-is-focusable="true"]',
              );
              if (firstInput) {
                (firstInput as HTMLElement).focus();
                break;
              }
            }
          }
        } else if (e.shiftKey && currentRowIndex > 0) {
          // If no input found in this cell and Shift+Tab, try to move to previous row
          const previousRow = allRows[currentRowIndex - 1];
          const previousRowCells = previousRow.querySelectorAll('[data-automation-key]');
          if (previousRowCells.length > 0) {
            for (let i = previousRowCells.length - 1; i >= 0; i--) {
              const lastCell = previousRowCells[i];
              const lastInput = lastCell.querySelector(
                'input, select, button, [role="combobox"], [data-is-focusable="true"]',
              );
              if (lastInput) {
                (lastInput as HTMLElement).focus();
                break;
              }
            }
          }
        }
      } else if (nextIndex < 0 && currentRowIndex > 0) {
        // Shift+Tab: Move to last cell of previous row
        e.preventDefault();
        const previousRow = allRows[currentRowIndex - 1];
        const previousRowCells = previousRow.querySelectorAll('[data-automation-key]');
        if (previousRowCells.length > 0) {
          // Find the last cell with an interactive element
          for (let i = previousRowCells.length - 1; i >= 0; i--) {
            const lastCell = previousRowCells[i];
            const lastInput = lastCell.querySelector(
              'input, select, button, [role="combobox"], [data-is-focusable="true"]',
            );
            if (lastInput) {
              (lastInput as HTMLElement).focus();
              break;
            }
          }
        }
      } else if (nextIndex >= cells.length && currentRowIndex < allRows.length - 1) {
        // Tab: Move to first cell of next row
        e.preventDefault();
        const nextRow = allRows[currentRowIndex + 1];
        const nextRowCells = nextRow.querySelectorAll('[data-automation-key]');
        if (nextRowCells.length > 0) {
          // Find the first cell with an interactive element
          for (let i = 0; i < nextRowCells.length; i++) {
            const firstCell = nextRowCells[i];
            const firstInput = firstCell.querySelector(
              'input, select, button, [role="combobox"], [data-is-focusable="true"]',
            );
            if (firstInput) {
              (firstInput as HTMLElement).focus();
              break;
            }
          }
        }
      }
    }
  };

  return (
    <Stack styles={glassContainerStyles}>
      <Stack tokens={stackTokens15}>
        <div
          style={{
            paddingRight: '25px',
            paddingLeft: '25px',
          }}
        >
          <Stack horizontal horizontalAlign='space-between'>
            <TextField
              label='Filter op naam:'
              type='text'
              onChange={onSearchValueChange}
              value={filter || ''}
            />
            <Toggle
              label='Enable drag & drop'
              checked={enableDragDrop}
              onChange={(e, checked) => setEnableDragDrop(!!checked)}
              styles={{
                root: {
                  marginTop: '18px',
                },
                pill: {
                  backgroundColor: 'transparent',
                  borderColor: theme.palette.themePrimary,
                },
                thumb: {
                  backgroundColor: theme.palette.themePrimary,
                },
              }}
            />
          </Stack>
        </div>

        <div
          onKeyDown={handleDetailsListKeyDown}
          role='grid'
          aria-label='Construction sheet overview'
          tabIndex={0}
        >
          <ShimmeredDetailsList
            items={sortProperties(properties)}
            compact
            columns={columns}
            selection={_selection}
            selectionMode={SelectionMode.single}
            layoutMode={DetailsListLayoutMode.justified}
            onItemInvoked={editItem}
            isHeaderVisible
            getKey={getKey}
            setKey='none'
            dragDropEvents={_getDragDropEvents()}
            onRenderRow={onRenderRow}
            styles={{
              root: {
                backgroundColor: 'transparent',
                selectors: {
                  '.ms-DetailsHeader': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  },
                  '.ms-DetailsHeader-cell': {
                    color: theme.palette.themePrimary,
                    fontWeight: 600,
                  },
                  '.ms-DetailsRow': {
                    selectors: {
                      '&:focus': {
                        outline: 'none',
                      },
                      '&:focus-within': {
                        outline: `2px solid ${theme.palette.themePrimary}`,
                        outlineOffset: '-2px',
                      },
                    },
                  },
                },
              },
            }}
          />
        </div>
      </Stack>

      <Modal
        titleAriaId='Toon opmerkingen'
        isOpen={isModalOpen}
        onDismiss={toggleModal}
        isModeless
        containerClassName={modalContentStyles.container}
        styles={{
          main: {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '8px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
        }}
      >
        <div className={modalContentStyles.header}>
          <span>Opmerkingen</span>
          <IconButton
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel='Sluit venster'
            onClick={toggleModal}
          />
        </div>

        <div className={modalContentStyles.body}>
          {selectedItem ? (
            <ConstructionSheetRemarks
              item={selectedItem}
              suggestedvalues={suggestedvalues}
            />
          ) : null}
        </div>
      </Modal>
    </Stack>
  );
};

export default ConstructionSheetOverview;
